/**
 * InsuranceLoansRecords — every premium year in the estate, on the instrument it is recorded on.
 *
 * WHOLE-TABLE, CLIENT-SIDE [D-B46 R9], and that replaces a paginated read with two defects in it.
 * The instrument is a correlated aggregate compared against a sibling column — `SUM(deposits) >=
 * amount − 0.005` — which PostgREST cannot express, so filtering by it has to happen here. And the
 * old read counted with a separate `head: true` request that shared no filter with the page query:
 * the search box had been dead since it was added (typing reset the page and nothing else), and any
 * filter added to one query and not the other would have made `totalPages` lie. One read, one
 * array, one set of derivations — the two cannot disagree. `premiumLedger.ts` holds every rule and
 * is tested; this file draws them. The unbounded-fetch threshold is named in that file's docblock.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ListPagination } from '@/components/ListPagination';
import { useDebounce } from '@/hooks/useDebounce';
import { useServerPagination } from '@/hooks/useServerPagination';
import { cn } from '@/lib/utils';
import { formatBusinessDate, formatCurrency } from '@/lib/format';
import { Search } from 'lucide-react';
import {
    PREMIUM_INSTRUMENT_LABELS,
    PREMIUM_YEARS_KEY,
    fetchPremiumYears,
    matchesInstrumentFilter,
    matchesPremiumSearch,
    premiumRateLabel,
    sortPremiumYears,
    type PremiumInstrumentFilter,
} from './premiumLedger';

export default function InsuranceLoansRecords() {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);
    const [instrumentFilter, setInstrumentFilter] = useState<PremiumInstrumentFilter>('all');

    const { data: premiumYears, isLoading, error } = useQuery({
        queryKey: PREMIUM_YEARS_KEY,
        queryFn: fetchPremiumYears,
    });

    const rows = useMemo(
        () =>
            sortPremiumYears(
                (premiumYears ?? []).filter(
                    (row) =>
                        matchesInstrumentFilter(row, instrumentFilter) &&
                        matchesPremiumSearch(row, debouncedSearch),
                ),
            ),
        [premiumYears, instrumentFilter, debouncedSearch],
    );

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage } =
        useServerPagination({ totalCount: rows.length });

    // A narrowed list is a shorter list, so page 4 of the unfiltered estate is off the end of it.
    useEffect(() => { resetPage(); }, [debouncedSearch, instrumentFilter]);

    const page = rows.slice(from, to + 1);
    // Two empty states, because they mean different things: nothing recorded at all, or nothing
    // left after the admin narrowed it. An estate with no premium years in it reads as the former
    // whatever the controls say — a filter cannot be what emptied a list that was already empty.
    const narrowed =
        (premiumYears?.length ?? 0) > 0 &&
        (instrumentFilter !== 'all' || debouncedSearch.trim().length > 0);

    // A FAILED OR TRUNCATED READ DRAWS NO TABLE AT ALL [R11]. `fetchPremiumYears` throws when the
    // response came back shorter than the server's own count, and the whole point of that throw is
    // that a page of rows off an incomplete estate looks exactly like a complete one. Everything
    // below — the search, the filter, the pager — narrows a list; there is no list to narrow.
    if (error) {
        return (
            <Card className="border-0">
                <CardContent>
                    <p className="text-center text-destructive py-8">{error.message}</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="border-0">
            <CardContent className="space-y-4">
                {/* The controls row — search and the Instrument filter side by side, in the card
                    rather than the page header. */}
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search plate, vehicle, policy or contract..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <Select
                        value={instrumentFilter}
                        onValueChange={(value) => setInstrumentFilter(value as PremiumInstrumentFilter)}
                    >
                        <SelectTrigger className="w-[180px] shrink-0">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All instruments</SelectItem>
                            <SelectItem value="advance">Advances</SelectItem>
                            <SelectItem value="paid-in-full">Paid in full</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <ListPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={goToPage}
                    totalItems={totalItems}
                />

                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : page.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Policy start</TableHead>
                                <TableHead>Insurance</TableHead>
                                <TableHead>Vehicle</TableHead>
                                <TableHead>Contract</TableHead>
                                <TableHead>Instrument</TableHead>
                                <TableHead className="text-right">Rate</TableHead>
                                <TableHead className="text-right">Premium</TableHead>
                                <TableHead className="text-right">Advanced</TableHead>
                                <TableHead className="text-right">Paid by driver</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {page.map((row) => (
                                <TableRow key={row.loanId}>
                                    <TableCell>{formatBusinessDate(row.policyStart)}</TableCell>
                                    <TableCell>{row.insuranceId === null ? '-' : `#${row.insuranceId}`}</TableCell>
                                    <TableCell>
                                        {row.registration || row.make || row.model ? (
                                            <div>
                                                <span className="font-medium">{row.registration || 'Unregistered'}</span>
                                                <p className="text-xs text-muted-foreground">
                                                    {[row.make, row.model].filter(Boolean).join(' ')}
                                                </p>
                                            </div>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {row.contractId === null ? '-' : (
                                            <Link
                                                to={`/dashboard/contracts/${row.contractId}`}
                                                className="text-primary hover:underline"
                                            >
                                                #{row.contractId}
                                            </Link>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {/* The emerald/blue pair the contract's Insurance tab already
                                            uses for the same two shapes. */}
                                        <Badge
                                            variant="secondary"
                                            className={cn(
                                                'font-normal text-xs px-2 py-0 h-5',
                                                row.instrument === 'PAYMENT_RECORD'
                                                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                                                    : 'bg-blue-50 text-blue-700 hover:bg-blue-50',
                                            )}
                                        >
                                            {PREMIUM_INSTRUMENT_LABELS[row.instrument]}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        {premiumRateLabel(row)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {row.premium === null ? '-' : formatCurrency(row.premium)}
                                    </TableCell>
                                    <TableCell className="text-right font-medium">
                                        {formatCurrency(row.advanced)}
                                    </TableCell>
                                    {/* Blank rather than a zero or a dash: 283 of the 308 premium
                                        years carry no deposit at all, and a column of placeholders
                                        hides the 25 that do. */}
                                    <TableCell className="text-right text-muted-foreground">
                                        {row.depositTotal > 0 ? formatCurrency(row.depositTotal) : ''}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-center text-muted-foreground py-8">
                        {narrowed ? 'No premium years match this filter' : 'No premium years found'}
                    </p>
                )}
            </CardContent>
        </Card>
    );
}
